"use client";

import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";
import {
  resolveActiveTheme,
  THEME_LIGHT_CLASS,
  THEME_STORAGE_KEY,
  THEME_SWITCHING_CLASS,
} from "@/lib/theme-init-script";
import { writeStorage } from "@/lib/safe-storage";

/**
 * ThemeToggle — reads/writes localStorage 'tls.theme'.
 * The pre-paint script in app/layout.tsx applies the initial class synchronously
 * on <html> before React hydrates. This component hydrates into that state and
 * keeps it in sync on toggle.
 *
 * PRODUCT.md: dark is default, light is equal-quality secondary.
 * Toggle is quiet: icon-only button, no theatrical animation.
 */
export function ThemeToggle() {
  // Start undefined to avoid hydration mismatch — the html class is set by
  // the pre-paint script, so we read it on mount rather than assume dark.
  const [theme, setTheme] = useState<"dark" | "light" | undefined>(undefined);

  useEffect(() => {
    // Resolve from the STORED preference, not the <html> class. This effect can
    // run in the window where React's hydration pass has stripped `.light` and
    // the bootstrap observer has not yet put it back, so reading the class here
    // would leave the button's state inverted against what the user sees —
    // their first click would then appear to do nothing (TES-1578).
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing from an external system (storage / DOM) on mount is valid here
    setTheme(resolveActiveTheme());
  }, []);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    // Persist BEFORE mutating the class. The pre-paint bootstrap
    // (lib/theme-init-script.ts) re-asserts the stored preference from a
    // MutationObserver on <html>'s class, so writing the class first would
    // briefly leave storage and the DOM disagreeing (TES-1578).
    // A failed write is silent: the bootstrap falls back to trusting the DOM
    // when storage is unreadable, so the toggle still works for the session.
    writeStorage(THEME_STORAGE_KEY, next);
    const root = document.documentElement;
    // Suppress every transition for the duration of the flip. A theme change
    // repaints the whole document at once, so any element carrying a
    // colour transition animates independently and you see its SHAPE resolve
    // out of the old palette: the rounded nav pill was visibly morphing in
    // the top-left corner. Principle 6 covers this. Motion confirms a state
    // change the user caused in THAT element; a global repaint animating
    // every surface is motion performing.
    //
    // Two rAFs, not one: the first lands before the browser has recalculated
    // style for the class change, so removing the guard there would restore
    // transitions in the same frame the colours change and the flash
    // returns. The second fires after that paint.
    root.classList.add(THEME_SWITCHING_CLASS);
    if (next === "light") {
      root.classList.add(THEME_LIGHT_CLASS);
    } else {
      root.classList.remove(THEME_LIGHT_CLASS);
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => root.classList.remove(THEME_SWITCHING_CLASS));
    });
  };

  // Render a stable placeholder during SSR / pre-hydration to avoid layout shift.
  // The icon resolves after first useEffect runs (client-only).
  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-icon-btn
      data-cuelume-toggle="toggle"
      aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
      title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
      // §12.10: under reduced motion the scale press below is withheld by
      // `motion-safe:`, so `motion-reduce:active:bg-surface-hover` fills the
      // gap — background darkens instantly instead, per the spec's reduced-
      // motion row for press feedback. The transparent ground is a CLASS for
      // that reason: an inline `background` outranks the `active:` utility.
      className="tlsr-focus bg-transparent rounded-[var(--ls-radius-round)] transition-[scale,background-color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] motion-safe:active:scale-[0.97] motion-reduce:active:bg-surface-hover"
      style={{
        width: 44,
        height: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--tls-fg-muted)",
        border: "none",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {/* Moon shown in dark mode (click to go light); Sun shown in light mode.
          18px (not 17): inside the 44px hit area an odd size lands on a
          half-pixel ((44-17)/2 = 13.5), the glyph rounds off-center and the
          2px strokes blur on standard-DPI screens. 18px gives whole-pixel
          13px margins and matches the nav's other 18px icons.

          Moon gets a 1px up-right OPTICAL correction: lucide's crescent is
          bbox-centered in its viewBox, but its visible mass hangs low-left
          (the tips point at the empty top-right), so a geometrically centered
          moon reads off-center. Verified against a crosshair fixture; the Sun
          is radially symmetric and needs none. Don't "simplify" this away. */}
      {theme === "light" ? (
        <Sun style={{ width: 18, height: 18 }} aria-hidden="true" />
      ) : (
        <Moon
          style={{ width: 18, height: 18, transform: "translate(1px, -1px)" }}
          aria-hidden="true"
        />
      )}
    </button>
  );
}
